// @ts-nocheck
import { getCurrentInstance, h } from 'vue'
import { Loading } from '@jd/jdesign-icons-vue'
import JdCheckbox from '@jdesign-vue/components/checkbox'
import { JdIcon } from '@jdesign-vue/components/icon'
import { getProp } from '@jdesign-vue/utils'
import { MinusSquare, PlusSquare } from './config-expandIcon'
import SelectionDropdown from './selection-dropdown.vue'

import type { VNode } from 'vue'
import type { TableColumnCtx } from './table-column/defaults'
import type { Store } from './store'
import type { Table, TreeNode } from './table/defaults'

const defaultClassNames = {
  selection: 'table-column--selection',
  expand: 'table__expand-column',
}

export const cellStarts = {
  default: {
    order: '',
  },
  selection: {
    width: 48,
    minWidth: 48,
    realWidth: 48,
    order: '',
  },
  expand: {
    width: 48,
    minWidth: 48,
    realWidth: 48,
    order: '',
  },
  index: {
    width: 48,
    minWidth: 48,
    realWidth: 48,
    order: '',
  },
}

export const getDefaultClassName = (type) => {
  return defaultClassNames[type] || ''
}

export const cellForced = {
  selection: {
    renderHeader<T>({ store }: { store: Store<T> }) {
      const table = getCurrentInstance()?.parent as Table<T>
      const showDropdown = table?.props?.showSelectCurrentOrAllPage
      function isDisabled() {
        return store.states.data.value && store.states.data.value.length === 0
      }

      if (showDropdown) {
        return h(SelectionDropdown, {
          modelValue: store.states.isAllSelected.value,
          indeterminate:
            store.states.selection.value.length > 0 &&
            !store.states.isAllSelected.value,
          disabled: isDisabled(),
          size: store.states.tableSize.value,
          onToggleAllSelection: store.toggleAllSelection,
          onSelectAllData: (value: boolean) => {
            table.emit('select-all-data', value)
          },
          onCurrentPageToggle: (value: boolean) => {
            table.emit('current-page-toggle', value)
          },
        })
      }
      return h(JdCheckbox, {
        disabled: isDisabled(),
        size: store.states.tableSize.value,
        indeterminate:
          store.states.selection.value.length > 0 &&
          !store.states.isAllSelected.value,
        'onUpdate:modelValue': store.toggleAllSelection,
        modelValue: store.states.isAllSelected.value,
      })
    },
    renderCell<T>({
      row,
      column,
      store,
      $index,
    }: {
      row: T
      column: TableColumnCtx<T>
      store: Store<T>
      $index: string
    }) {
      return h(JdCheckbox, {
        disabled: column.selectable
          ? !column.selectable.call(null, row, $index)
          : false,
        size: store.states.tableSize.value,
        onChange: () => {
          store.commit('rowSelectedChanged', row)
        },
        onClick: (event: Event) => event.stopPropagation(),
        modelValue: store.isSelected(row),
      })
    },
    sortable: false,
    resizable: false,
  },
  index: {
    renderHeader<T>({ column }: { column: TableColumnCtx<T> }) {
      return column.label || '#'
    },
    renderCell<T>({
      column,
      $index,
    }: {
      column: TableColumnCtx<T>
      $index: number
    }) {
      let i = $index + 1
      const index = column.index

      if (typeof index === 'number') {
        i = $index + index
      } else if (typeof index === 'function') {
        i = index($index)
      }
      return h('div', {}, [i])
    },
    sortable: false,
  },
  expand: {
    renderHeader<T>({ column }: { column: TableColumnCtx<T> }) {
      return column.label || ''
    },
    renderCell<T>({
      row,
      store,
      expanded,
    }: {
      row: T
      store: Store<T>
      expanded: boolean
    }) {
      const { ns } = store
      const icon = expanded ? MinusSquare : PlusSquare
      const classes = [ns.e('expand-icon')]
      if (expanded) {
        classes.push(ns.em('expand-icon', 'expanded'))
      }
      const callback = function (e: Event) {
        e.stopPropagation()
        store.toggleRowExpansion(row)
      }
      return h(
        'div',
        {
          class: classes,
          onClick: callback,
          'data-component-name': 'Table',
          'data-event-name': expanded ? 'shrink' : 'expand',
          'data-event-content': '',
        },
        {
          default: () => {
            return [
              h(JdIcon, null, {
                default: () => {
                  return [h(icon)]
                },
              }),
            ]
          },
        }
      )
    },
    sortable: false,
    resizable: false,
  },
}

export function defaultRenderCell<T>({
  row,
  column,
  $index,
}: {
  row: T
  column: TableColumnCtx<T>
  $index: number
}) {
  const property = column.property
  const value = property && getProp(row, property).value
  if (column && column.formatter) {
    return column.formatter(row, column, value, $index)
  }
  return value?.toString?.() || ''
  // return [
  //   h(
  //     'div',
  //     {
  //       class: 'default-cell-wrapper',
  //     },
  //     [value?.toString?.() || '']
  //   ),
  // ]
}

export function treeCellPrefix<T>(
  {
    row,
    treeNode,
    store,
  }: {
    row: T
    treeNode: TreeNode
    store: Store<T>
  },
  createPlaceholder = false
) {
  const { ns } = store
  if (!treeNode) {
    if (createPlaceholder) {
      return [
        h('span', {
          class: ns.e('placeholder'),
        }),
      ]
    }
    return null
  }
  const ele: VNode[] = []
  const callback = function (e) {
    e.stopPropagation()
    if (treeNode.loading) {
      return
    }
    store.loadOrToggle(row)
  }
  if (treeNode.indent) {
    ele.push(
      h('span', {
        class: ns.e('indent'),
        style: { 'padding-left': `${treeNode.indent}px` },
      })
    )
  }
  if (typeof treeNode.expanded === 'boolean' && !treeNode.noLazyChildren) {
    const expandClasses = [
      ns.e('expand-icon'),
      treeNode.expanded ? ns.em('expand-icon', 'expanded') : '',
    ]
    let icon = treeNode.expanded ? MinusSquare : PlusSquare
    if (treeNode.loading) {
      icon = Loading
    }

    ele.push(
      h(
        'div',
        {
          class: expandClasses,
          onClick: callback,
          'data-component-name': 'Table',
          'data-event-name': treeNode.expanded ? 'shrink' : 'expand',
          'data-event-content': '',
        },
        {
          default: () => {
            return [
              h(
                JdIcon,
                { class: { [ns.is('loading')]: treeNode.loading } },
                {
                  default: () => [h(icon)],
                }
              ),
            ]
          },
        }
      )
    )
  } else {
    ele.push(
      h('span', {
        class: ns.e('placeholder'),
      })
    )
  }
  return ele
}
